import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Link,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Pagination,
  useMediaQuery,
  useTheme,
  FormControlLabel,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Fade,
  Zoom,
  alpha,
} from '@mui/material';
import {
  Edit,
  History,
  Gavel,
  Add,
  Download,
  Search,
  AccountBalance,
  CreditCard,
  Badge,
  Refresh,
} from '@mui/icons-material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { debounce } from 'lodash';
import {
  useLazyGetBankDetailsQuery,
  useCreateBankDetailsMutation,
  useUpdateBankDetailsMutation,
  useDownloadExcelMutation,
  useGetGatewaysQuery,
} from '../services/bankApi';
import { BankFormDialog } from '../components/BankFormDialog';
import { BankHistoryDialog } from '../components/BankHistoryDialog';
import { BankBlacklistDialog } from '../components/BankBlacklistDialog';
import BlacklistManager from '../components/BlacklistManager';
import {
  AggregatorOption,
  BankRow,
  BlacklistEntry,
  getGatewayParam,
  mapToBankRow,
} from '../types/bankdetails';

export default function BankDetails() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [rows, setRows] = useState<BankRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false);
  const [openBlacklistDialog, setOpenBlacklistDialog] = useState(false);
  const [openBlacklistManager, setOpenBlacklistManager] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [selectedBankForHistory, setSelectedBankForHistory] = useState<BankRow | null>(null);
  const [selectedBankForBlacklist, setSelectedBankForBlacklist] = useState<BankRow | null>(null);
  const [selectedBlacklistEntry, setSelectedBlacklistEntry] = useState<BlacklistEntry | null>(null);

  // API Hooks
  const [triggerGetBankDetails, { isFetching }] = useLazyGetBankDetailsQuery();
  const [createBankDetails] = useCreateBankDetailsMutation();
  const [updateBankDetails] = useUpdateBankDetailsMutation();
  const [downloadExcel] = useDownloadExcelMutation();
  const {
    data: gatewaysData,
    isLoading: gatewaysLoading,
    error: gatewaysError,
  } = useGetGatewaysQuery();

  // Compute default gateway separately to avoid unnecessary recomputes
  const defaultGateway = useMemo(() => {
    const availableGateways = gatewaysData?.gateways || [];
    return availableGateways.length > 0 ? availableGateways[0] : 'Billdesk V1';
  }, [gatewaysData]);

  // URL Params
  const searchParamsValues = useMemo(() => {
    return {
      page: parseInt(searchParams.get('page') || '1', 10),
      pageSize: parseInt(searchParams.get('pageSize') || '20', 10),
      aggregator: (searchParams.get('aggregator') as AggregatorOption) || defaultGateway,
      ifscFilter: searchParams.get('ifsc') || '',
    };
  }, [searchParams, defaultGateway]);

  const { page, pageSize, aggregator, ifscFilter } = searchParamsValues;

  const [ifscInputValue, setIfscInputValue] = useState(ifscFilter); // Separate input value from filter

  // Extracted refreshData utility - reusable fetch + map + setState logic
  const refreshData = useCallback(async () => {
    const res = await triggerGetBankDetails({
      gateway: getGatewayParam(aggregator),
      ifsc: ifscFilter.trim() || undefined,
      page,
      limit: pageSize,
    });
    if ('data' in res && res.data) {
      const payload: any = res.data;
      const raw = payload.data ?? payload;
      const mapped: BankRow[] = Array.isArray(raw)
        ? raw.map((it: any, idx: number) => mapToBankRow(it, idx))
        : [];
      setRows(mapped);
      setTotalCount(payload.meta?.total ?? payload.data?.length ?? 0);
    }
  }, [triggerGetBankDetails, aggregator, ifscFilter, page, pageSize]);

  // Update Search Params
  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          if (value === null || value === '' || value === undefined) {
            newParams.delete(key);
          } else {
            newParams.set(key, value);
          }
        });
        return newParams;
      });
    },
    [setSearchParams]
  );

  // Debounced IFSC Search
  const debouncedIfscSearch = useMemo(
    () =>
      debounce((value: string) => {
        updateSearchParams({ ifsc: value || null, page: '1' });
      }, 500),
    [updateSearchParams]
  );

  // Cleanup debounce on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      debouncedIfscSearch.cancel();
    };
  }, [debouncedIfscSearch]);

  // Handle IFSC input change
  const handleIfscInputChange = useCallback(
    (value: string) => {
      setIfscInputValue(value); // Update input value immediately for UI
      debouncedIfscSearch(value); // Debounce the actual API call
    },
    [debouncedIfscSearch]
  );

  // Sync input value with URL params
  useEffect(() => {
    setIfscInputValue(ifscFilter);
  }, [ifscFilter]);

  // Handlers
  const handlePageChange = useCallback(
    (newPage: number) => {
      updateSearchParams({ page: newPage.toString() });
    },
    [updateSearchParams]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      updateSearchParams({ pageSize: newPageSize.toString(), page: '1' });
    },
    [updateSearchParams]
  );

  const handleAggregatorChange = useCallback(
    (newAggregator: AggregatorOption) => {
      updateSearchParams({ aggregator: newAggregator, page: '1' });
    },
    [updateSearchParams]
  );

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const filteredRows = useMemo(() => rows, [rows]);

  const handleDownload = async () => {
    try {
      const gateway = getGatewayParam(aggregator);
      const blob = await downloadExcel({
        gateway,
        ifsc: ifscFilter.trim() || undefined,
        bankName: undefined,
      }).unwrap();
      const dlUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = dlUrl;
      link.setAttribute('download', 'bank-details.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(dlUrl);
    } catch (e) {
      console.error('Download failed', e);
    }
  };

  const handleAddBank = async (formData: any) => {
    if (!formData.name || !formData.ifscCode) {
      return;
    }

    try {
      const bankData: any = {
        gateway: getGatewayParam(aggregator),
        ifsc: formData.ifscCode,
        bankName: formData.name,
        remarks: formData.remarks,
        isActive: true,
        lumpsumNetBanking: !!formData.nbLumpsum,
        hybridNetBanking: !!formData.nbHybrid,
        lumpsumDebitCard: !!formData.nbDebitCard,
        emandateNetBanking: !!formData.emdNetBanking,
        emandateDebitCard: !!formData.emdDebitCard,
        emandateAadhar: !!formData.emdAadhaar,
        emandatePAN: !!formData.emdPan,
        otmNetBanking: !!formData.emdNetBanking,
        otmDebitCard: !!formData.emdDebitCard,
        lumpsumNetBankingBankId: formData.nbBankId || '',
        hybridNetBankingBankId: formData.nbHybridBankId || '',
        lumpsumDebitCardBankId: formData.nbDebitCardBankId || '',
        emandateBankId: formData.emdBankId || '',
      };

      await createBankDetails(bankData).unwrap();
      setOpenAddDialog(false);
      // Refresh the data
      await refreshData();
    } catch (error) {
      console.error('Failed to create bank:', error);
    }
  };

  const handleUpdateBank = async (formData: any) => {
    if (!formData.name || !formData.ifscCode || editingIndex === null) {
      return;
    }

    try {
      const current = rows[editingIndex];

      // Always include id, gateway, ifsc and remarks
      const bankData: any = {
        id: current.id.toString(),
        gateway: getGatewayParam(aggregator),
        ifsc: formData.ifscCode,
        remarks: formData.remarks,
      };

      // Conditionally include only changed fields
      if (typeof formData.name !== 'undefined' && formData.name !== current.name) {
        bankData.bankName = formData.name;
      }

      // Track whether any fields in each category changed
      const lumpsumCategoryChanged =
        typeof formData.nbLumpsum !== 'undefined' &&
        Boolean(formData.nbLumpsum) !== Boolean(current.netbanking?.lumpsum);
      const lumpsumBankIdChanged =
        typeof formData.nbBankId !== 'undefined' &&
        formData.nbBankId !== (current.netbanking?.bankId ?? '');
      const hybridCategoryChanged =
        typeof formData.nbHybrid !== 'undefined' &&
        Boolean(formData.nbHybrid) !== Boolean(current.netbanking?.hybrid);
      const hybridBankIdChanged =
        typeof formData.nbHybridBankId !== 'undefined' &&
        formData.nbHybridBankId !== (current.netbanking?.hybridBankId ?? '');
      const debitCardCategoryChanged =
        typeof formData.nbDebitCard !== 'undefined' &&
        Boolean(formData.nbDebitCard) !== Boolean(current.netbanking?.lumpsumDebitCard);
      const debitCardBankIdChanged =
        typeof formData.nbDebitCardBankId !== 'undefined' &&
        formData.nbDebitCardBankId !== (current.netbanking?.lumpsumDebitCardBankId ?? '');
      const emandateCategoryChanged =
        (typeof formData.emdNetBanking !== 'undefined' &&
          Boolean(formData.emdNetBanking) !== Boolean(current.emandate?.netBanking)) ||
        (typeof formData.emdDebitCard !== 'undefined' &&
          Boolean(formData.emdDebitCard) !== Boolean(current.emandate?.debitCard)) ||
        (typeof formData.emdAadhaar !== 'undefined' &&
          Boolean(formData.emdAadhaar) !== Boolean(current.emandate?.aadhaar)) ||
        (typeof formData.emdPan !== 'undefined' &&
          Boolean(formData.emdPan) !== Boolean(current.emandate?.pan));
      const emandateBankIdChanged =
        typeof formData.emdBankId !== 'undefined' &&
        formData.emdBankId !== (current.emandate?.bankId ?? '');

      // If a category changed, include its bankId mandatorily
      if (lumpsumCategoryChanged || lumpsumBankIdChanged) {
        bankData.lumpsumNetBankingBankId = formData.nbBankId ?? current.netbanking?.bankId ?? '';
      }

      if (hybridCategoryChanged || hybridBankIdChanged) {
        bankData.hybridNetBankingBankId =
          formData.nbHybridBankId ?? current.netbanking?.hybridBankId ?? '';
      }

      if (debitCardCategoryChanged || debitCardBankIdChanged) {
        bankData.lumpsumDebitCardBankId =
          formData.nbDebitCardBankId ?? current.netbanking?.lumpsumDebitCardBankId ?? '';
      }

      if (emandateCategoryChanged || emandateBankIdChanged) {
        bankData.emandateBankId = formData.emdBankId ?? current.emandate?.bankId ?? '';
      }

      if (
        typeof formData.nbLumpsum !== 'undefined' &&
        Boolean(formData.nbLumpsum) !== Boolean(current.netbanking?.lumpsum)
      ) {
        bankData.lumpsumNetBanking = !!formData.nbLumpsum;
      }

      if (
        typeof formData.nbHybrid !== 'undefined' &&
        Boolean(formData.nbHybrid) !== Boolean(current.netbanking?.hybrid)
      ) {
        bankData.hybridNetBanking = !!formData.nbHybrid;
      }

      if (
        typeof formData.nbDebitCard !== 'undefined' &&
        Boolean(formData.nbDebitCard) !== Boolean(current.netbanking?.lumpsumDebitCard)
      ) {
        bankData.lumpsumDebitCard = !!formData.nbDebitCard;
      }

      if (
        typeof formData.emdNetBanking !== 'undefined' &&
        Boolean(formData.emdNetBanking) !== Boolean(current.emandate?.netBanking)
      ) {
        bankData.emandateNetBanking = !!formData.emdNetBanking;
      }

      if (
        typeof formData.emdDebitCard !== 'undefined' &&
        Boolean(formData.emdDebitCard) !== Boolean(current.emandate?.debitCard)
      ) {
        bankData.emandateDebitCard = !!formData.emdDebitCard;
      }

      if (
        typeof formData.emdAadhaar !== 'undefined' &&
        Boolean(formData.emdAadhaar) !== Boolean(current.emandate?.aadhaar)
      ) {
        bankData.emandateAadhar = !!formData.emdAadhaar;
      }

      if (
        typeof formData.emdPan !== 'undefined' &&
        Boolean(formData.emdPan) !== Boolean(current.emandate?.pan)
      ) {
        bankData.emandatePAN = !!formData.emdPan;
      }

      await updateBankDetails(bankData).unwrap();
      setOpenEditDialog(false);
      setEditingIndex(null);
      // Refresh the data
      await refreshData();
    } catch (error) {
      console.error('Failed to update bank:', error);
    }
  };

  const openEditDialogForBank = (row: BankRow) => {
    setEditingIndex(rows.findIndex((r) => r.id === row.id));
    setOpenEditDialog(true);
  };

  const openHistoryDialogForBank = (bank: BankRow) => {
    setSelectedBankForHistory(bank);
    setOpenHistoryDialog(true);
  };

  const openBlacklistDialogForBank = (bank: BankRow) => {
    setSelectedBankForBlacklist(bank);
    setOpenBlacklistDialog(true);
  };

  return (
    <Box sx={{ width: '100%', p: 3, bgcolor: 'grey.50', minHeight: '100vh' }}>
      <Fade in timeout={300}>
        <Box>
          <Breadcrumbs
            separator="·"
            sx={{
              mb: 3,
              '& .MuiBreadcrumbs-separator': { mx: 1, color: theme.palette.text.secondary },
            }}
          >
            <Link
              component={RouterLink}
              to="/dashboard"
              sx={{
                color: theme.palette.primary.main,
                fontSize: theme.typography.subtitle1.fontSize,
                fontWeight: theme.typography.subtitle1.fontWeight,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              Dashboard
            </Link>
            <Typography variant="subtitle1">Bank Details</Typography>
          </Breadcrumbs>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'grey.900', mb: 1 }}>
              Bank Configuration Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage payment gateway configurations, bank details, and merchant blacklists
            </Typography>
          </Box>

          <Card sx={{ mb: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AccountBalance sx={{ mr: 2, color: 'primary.main', fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'grey.900' }}>
                  Payment Gateway Selection
                </Typography>
              </Box>
              <RadioGroup
                row
                value={aggregator}
                onChange={(e) => handleAggregatorChange(e.target.value as AggregatorOption)}
                sx={{ mb: 3 }}
              >
                {gatewaysLoading ? (
                  <Typography variant="body2" color="text.secondary">
                    Loading gateways...
                  </Typography>
                ) : gatewaysError ? (
                  <Typography variant="body2" color="error">
                    Error loading gateways. Please try again.
                  </Typography>
                ) : (
                  // Load gateways dynamically from API
                  gatewaysData?.gateways?.map((gateway: string) => (
                    <FormControlLabel 
                      key={gateway}
                      value={gateway} 
                      control={<Radio color="primary" />} 
                      label={gateway} 
                      sx={{ mx: 1 }}
                    />
                  ))
                )}
              </RadioGroup>

              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="medium"
                    label="Search by IFSC Code"
                    value={ifscInputValue}
                    onChange={(e) => handleIfscInputChange(e.target.value)}
                    InputProps={{
                      startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'stretch', md: 'flex-end' } }}>
                    <Tooltip title="Refresh Data">
                      <IconButton
                        color="primary"
                        onClick={() => refreshData()}
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) },
                        }}
                      >
                        <Refresh />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.08)', borderRadius: 3, overflow: 'visible' }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Badge sx={{ mr: 2, color: 'primary.main', fontSize: 28 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'grey.900' }}>
                      Bank Configurations
                    </Typography>
                    {isFetching && <CircularProgress size={20} sx={{ ml: 2 }} />}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {totalCount} total banks
                  </Typography>
                </Box>
              </Box>

              <TableContainer sx={{ maxHeight: 600 }}>
                <Table
                  stickyHeader
                  sx={{
                    '& .MuiTableCell-root': { 
                      borderColor: alpha(theme.palette.divider, 0.8),
                      py: 1.5
                    },
                    '& thead .MuiTableCell-root': {
                      bgcolor: alpha(theme.palette.grey[100], 0.8),
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: 'grey.700',
                      borderBottom: `2px solid ${theme.palette.divider}`
                    },
                    '& tbody .MuiTableRow:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.04)
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 60 }}>S.No</TableCell>
                      <TableCell sx={{ minWidth: 200 }}>Bank Name</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>IFSC Code</TableCell>
                      <TableCell
                        colSpan={6}
                        align="center"
                        sx={{ bgcolor: alpha(theme.palette.info.main, 0.1) }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                          }}
                        >
                          <AccountBalance fontSize="small" />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Netbanking
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        colSpan={5}
                        align="center"
                        sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                          }}
                        >
                          <CreditCard fontSize="small" />
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Emandate / OTM
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ minWidth: 100 }}>Actions</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.75rem', color: 'grey.600' }}>
                        Bank ID
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.75rem', color: 'grey.600' }}>
                        Lumpsum
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.75rem', color: 'grey.600' }}>
                        Bank ID
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.75rem', color: 'grey.600' }}>
                        Hybrid
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.75rem', color: 'grey.600' }}>
                        Bank ID
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.75rem', color: 'grey.600' }}>
                        Debit Card
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.75rem', color: 'grey.600' }}>
                        Bank ID
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.75rem', color: 'grey.600' }}>
                        Net Banking
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.75rem', color: 'grey.600' }}>
                        Debit Card
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.75rem', color: 'grey.600' }}>
                        Aadhaar
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: '0.75rem', color: 'grey.600' }}>
                        PAN
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={17} align="center" sx={{ py: 6 }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Search sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                              No banks found
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {ifscFilter ? `No results for "${ifscFilter}"` : 'No bank configurations available'}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredRows.map((row, idx) => (
                        <Zoom in timeout={300} key={row.id}>
                          <TableRow hover>
                            <TableCell sx={{ fontWeight: 500 }}>
                              {(page - 1) * pageSize + idx + 1}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Tooltip title="Edit bank details">
                                  <IconButton
                                    size="small"
                                    onClick={() => openEditDialogForBank(row)}
                                    sx={{ 
                                      color: 'primary.main',
                                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.1) }
                                    }}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    '&:hover': { color: 'primary.main' }
                                  }}
                                  onClick={() => openEditDialogForBank(row)}
                                >
                                  {row.name}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={row.ifscCode} 
                                size="small" 
                                variant="outlined"
                                sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                {row.netbanking?.bankId || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={row.netbanking?.lumpsum ? 'Enabled' : 'Disabled'}
                                size="small"
                                color={row.netbanking?.lumpsum ? 'success' : 'default'}
                                variant={row.netbanking?.lumpsum ? 'filled' : 'outlined'}
                                sx={{ fontWeight: 500, fontSize: '0.7rem' }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                {row.netbanking?.hybridBankId || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={row.netbanking?.hybrid ? 'Enabled' : 'Disabled'}
                                size="small"
                                color={row.netbanking?.hybrid ? 'success' : 'default'}
                                variant={row.netbanking?.hybrid ? 'filled' : 'outlined'}
                                sx={{ fontWeight: 500, fontSize: '0.7rem' }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Typography
                                variant="body2"
                                sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                              >
                                {row.netbanking?.lumpsumDebitCardBankId || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={row.netbanking?.lumpsumDebitCard ? 'Enabled' : 'Disabled'}
                                size="small"
                                color={row.netbanking?.lumpsumDebitCard ? 'success' : 'default'}
                                variant={row.netbanking?.lumpsumDebitCard ? 'filled' : 'outlined'}
                                sx={{ fontWeight: 500, fontSize: '0.7rem' }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Typography
                                variant="body2"
                                sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                              >
                                {row.emandate?.bankId || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={row.emandate?.netBanking ? 'Enabled' : 'Disabled'}
                                size="small"
                                color={row.emandate?.netBanking ? 'success' : 'default'}
                                variant={row.emandate?.netBanking ? 'filled' : 'outlined'}
                                sx={{ fontWeight: 500, fontSize: '0.7rem' }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={row.emandate?.debitCard ? 'Enabled' : 'Disabled'}
                                size="small"
                                color={row.emandate?.debitCard ? 'success' : 'default'}
                                variant={row.emandate?.debitCard ? 'filled' : 'outlined'}
                                sx={{ fontWeight: 500, fontSize: '0.7rem' }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={row.emandate?.aadhaar ? 'Enabled' : 'Disabled'}
                                size="small"
                                color={row.emandate?.aadhaar ? 'success' : 'default'}
                                variant={row.emandate?.aadhaar ? 'filled' : 'outlined'}
                                sx={{ fontWeight: 500, fontSize: '0.7rem' }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={row.emandate?.pan ? 'Enabled' : 'Disabled'}
                                size="small"
                                color={row.emandate?.pan ? 'success' : 'default'}
                                variant={row.emandate?.pan ? 'filled' : 'outlined'}
                                sx={{ fontWeight: 500, fontSize: '0.7rem' }}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                <Tooltip title="View History">
                                  <IconButton
                                    size="small"
                                    onClick={() => openHistoryDialogForBank(row)}
                                    sx={{ 
                                      color: 'info.main',
                                      '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.1) }
                                    }}
                                  >
                                    <History fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {row.blacklistedMerchants &&
                                  row.blacklistedMerchants.length > 0 && (
                                    <Tooltip
                                      title={`View ${row.blacklistedMerchants.length} blacklisted merchant(s)`}
                                    >
                                      <IconButton
                                        size="small"
                                        onClick={() => openBlacklistDialogForBank(row)}
                                        sx={{
                                          color: 'error.main',
                                          '&:hover': {
                                            bgcolor: alpha(theme.palette.error.main, 0.1),
                                          },
                                        }}
                                      >
                                        <Gavel fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        </Zoom>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          <Box sx={{ mt: 3, display: 'flex', gap: 2, flexDirection: isMobile ? 'column' : 'row' }}>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={handleDownload}
              sx={{ 
                flex: isMobile ? 1 : 'none',
                py: 1.5,
                px: 3,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                '&:hover': {
                  boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                }
              }}
            >
              Download Excel
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<Add />}
              onClick={() => setOpenAddDialog(true)}
              sx={{ 
                flex: isMobile ? 1 : 'none',
                py: 1.5,
                px: 3,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                '&:hover': {
                  boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)',
                }
              }}
            >
              Add New Bank
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<Gavel />}
              onClick={() => setOpenBlacklistManager(true)}
              sx={{ 
                flex: isMobile ? 1 : 'none',
                py: 1.5,
                px: 3,
                borderRadius: 2,
                fontWeight: 600,
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
                '&:hover': {
                  boxShadow: '0 6px 16px rgba(211, 47, 47, 0.4)',
                }
              }}
            >
              Manage Blacklist
            </Button>
          </Box>

          <Card sx={{ mt: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2,
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Showing {Math.min((page - 1) * pageSize + 1, totalCount)} to{' '}
                  {Math.min(page * pageSize, totalCount)} of {totalCount} entries
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <TextField
                    label="Rows per page"
                    select
                    SelectProps={{ native: true }}
                    size="small"
                    value={pageSize}
                    onChange={(e) =>
                      handlePageSizeChange(parseInt(e.target.value as unknown as string, 10))
                    }
                    sx={{
                      minWidth: 120,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  >
                    <option value={10}>10 rows</option>
                    <option value={20}>20 rows</option>
                    <option value={50}>50 rows</option>
                  </TextField>
                  <Pagination
                    count={Math.max(1, Math.ceil((totalCount || 0) / pageSize))}
                    page={page}
                    onChange={(_e, value) => handlePageChange(value)}
                    showFirstButton
                    showLastButton
                    size="medium"
                    color="primary"
                    sx={{
                      '& .MuiPaginationItem-root': {
                        borderRadius: 1,
                      },
                    }}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Fade>

      {/* Add New Bank Dialog */}
      <BankFormDialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        title="Add New Bank"
        onSubmit={handleAddBank}
        aggregator={aggregator}
        mode="add"
      />

      {/* Edit Bank Dialog */}
      <BankFormDialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        title="Update Bank Details"
        onSubmit={handleUpdateBank}
        aggregator={aggregator}
        mode="edit"
        editingIndex={editingIndex}
        rows={rows}
        onOpenHistory={openHistoryDialogForBank}
      />

      {/* Bank History Dialog */}
      <BankHistoryDialog
        open={openHistoryDialog}
        onClose={() => setOpenHistoryDialog(false)}
        selectedBank={selectedBankForHistory}
        aggregator={aggregator}
      />

      {/* Bank Blacklist Dialog */}
      <BankBlacklistDialog
        open={openBlacklistDialog}
        onClose={() => setOpenBlacklistDialog(false)}
        bank={selectedBankForBlacklist}
        onEdit={(entry) => {
          setSelectedBlacklistEntry(entry);
          setOpenBlacklistDialog(false);
          setOpenBlacklistManager(true);
        }}
      />

      <BlacklistManager
        open={openBlacklistManager}
        onClose={() => {
          setOpenBlacklistManager(false);
          setSelectedBlacklistEntry(null);
        }}
        gateway={getGatewayParam(aggregator)}
        initialData={selectedBlacklistEntry}
        onSuccess={refreshData}
      />
    </Box>
  );
}
